import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { on } from '@ember/modifier';
import { task } from 'ember-concurrency';
import { or, eq, not } from 'ember-truth-helpers';
import { t } from 'ember-intl';
import can from 'ember-can/helpers/can';
import set from 'ember-set-helper/helpers/set';
import {
  HdsPageHeader,
  HdsDropdown,
  HdsTextDisplay,
  HdsButton,
  HdsApplicationState,
} from '@hashicorp/design-system-components/components';
import { CoreLayoutMain, BreadcrumbsContainer, Flex } from 'core/components';
import { boxPadding } from 'core/modifiers';
import RoleDefinitions from 'manage-access/components/manage-access/flyout/role-definitions';
import SummaryCard from 'hcp/components/iam/service-principals/summary-card';
import RolesTableV2 from 'hcp/components/iam/service-principals/roles-table-v2';
import DeleteModal from 'hcp/components/service-principal/delete-modal';

/**
 * Contains the `servicePrincipal` and 'project'.
 * @argument model
 * @type {Object}
 */
/**
 *
 * `PageServicePrincipalsProjectDetail` renders the details of a project Service Principal
 *
 *
 * ```
 * <Page::ServicePrincipals::Project::Detail
 *   @model={{model}}
 * />
 * ```
 *
 * @class PageServicePrincipalsProjectDetail
 *
 */
export default class PageServicePrincipalsProjectDetail extends Component {
  @service api;
  @service flashMessages;
  @service intl;
  @service router;
  @service quota;

  @tracked showDeleteModal = false;
  @tracked showRoleDefinitionsFlyout = false;

  deleteServicePrincipal = task(async () => {
    const { id, organizationId, projectId } = this.args.model.servicePrincipal;
    const response =
      await this.api.servicePrincipal.servicePrincipalsServiceDeleteProjectServicePrincipal(
        organizationId,
        projectId,
        id
      );
    this.onDeleteServicePrincipalSuccess({
      response,
      id,
    });
  });

  onDeleteServicePrincipal = () => {
    this.showDeleteModal = true;
  };

  resetDeleteServicePrincipal = () => {
    this.showDeleteModal = false;
  };

  onDeleteServicePrincipalSuccess = () => {
    this.resetDeleteServicePrincipal();
    this.flashMessages.success(
      this.intl.t(
        'components.page.access-control.service-principals.delete-modal.success'
      )
    );
    this.router.transitionTo('cloud.access-control.service-principals.list');
  };

  <template>
    <CoreLayoutMain>
      <:header>
        <HdsPageHeader as |PH|>
          <PH.Breadcrumb>
            <BreadcrumbsContainer />
          </PH.Breadcrumb>
          <PH.IconTile @icon="robot" />
          <PH.Title>
            {{@model.servicePrincipal.name}}
          </PH.Title>
          {{#let
            (can "update service-principal") (can "delete service-principal")
            as |canUpdate canDelete|
          }}
            {{#if (or canUpdate canDelete)}}
              <PH.Actions>
                <HdsDropdown
                  @listPosition="bottom-right"
                  data-test-more-menu
                  as |dd|
                >
                  <dd.ToggleButton
                    @color="secondary"
                    @text={{t "components.form.manage"}}
                    data-test-more-menu-trigger
                    data-test-manage-service-principal
                  />
                  {{#if canUpdate}}
                    <dd.Interactive
                      @icon="edit"
                      @route="cloud.access-control.service-principals.detail.edit"
                      @text={{t
                        "components.page.access-control.service-principals.details.actions.menu.edit"
                      }}
                      data-test-edit-service-principal
                    />
                  {{/if}}
                  {{#if canDelete}}
                    <dd.Interactive
                      @color="critical"
                      @icon="trash"
                      @text={{t
                        "components.page.access-control.service-principals.details.actions.menu.delete"
                      }}
                      data-test-delete-service-principal
                      {{on "click" this.onDeleteServicePrincipal}}
                    />
                  {{/if}}
                </HdsDropdown>
              </PH.Actions>
            {{/if}}
          {{/let}}
        </HdsPageHeader>
      </:header>
      <:alert>
        <SummaryCard @servicePrincipal={{@model.servicePrincipal}} />
      </:alert>
      <:body>
        <Flex @direction="column" @gap="sm">
          <HdsTextDisplay @tag="h2" @size="400">
            {{t
              "components.page.access-control.service-principals.details.roles"
            }}
          </HdsTextDisplay>
          {{#if (or (not (eq @model.roleLocations.length 0)) @model.role)}}

            <Flex @alignItems="center" @direction="row" @justifyContent="end">
              <HdsButton
                @color="tertiary"
                @icon="help"
                @iconPosition="leading"
                @isInline={{true}}
                @text={{t
                  "components.page.access-control.service-principals.details.view-role-definitions"
                }}
                {{on "click" (set this "showRoleDefinitionsFlyout" true)}}
                data-test-roles-chart-toggle-button
              />
            </Flex>
            <RolesTableV2
              @scope={{@model.project.name}}
              @roleLocations={{@model.roleLocations}}
              @type={{@model.resourceType}}
            />
          {{else}}
            <HdsApplicationState
              {{boxPadding "2xl 0"}}
              data-test-service-principal-detail-roles-table-application-zero-state
              as |A|
            >
              <A.Header
                @title={{t
                  "components.page.access-control.service-principals.details.no-roles.title"
                }}
              />
              <A.Body
                @text={{t
                  "components.page.access-control.service-principals.details.no-roles.can-edit.description"
                }}
              />
              <A.Footer as |F|>
                <F.LinkStandalone
                  @icon="arrow-right"
                  @iconPosition="trailing"
                  @route="cloud.access-control.service-principals.detail.edit"
                  @text={{t
                    "components.page.access-control.service-principals.details.no-roles.can-edit.edit-link"
                  }}
                />

              </A.Footer>
            </HdsApplicationState>
          {{/if}}
        </Flex>
        {{#if this.showDeleteModal}}
          <DeleteModal
            @onCancel={{this.resetDeleteServicePrincipal}}
            @onSubmit={{this.deleteServicePrincipal}}
            @servicePrincipalToDelete={{@model.servicePrincipal}}
            @keys={{@model.keys}}
            @wips={{@model.workloadIdentityProviders}}
          />
        {{/if}}
        {{#if this.showRoleDefinitionsFlyout}}
          <RoleDefinitions
            @rolesLocation={{@model.currentRolesLocation}}
            @currentScopeLink={{@model.currentScopeLink}}
            @onClose={{set this "showRoleDefinitionsFlyout" false}}
          />
        {{/if}}
      </:body>
    </CoreLayoutMain>
  </template>
}
